import React, { useState, useEffect, useRef } from "react";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  updateDoc,
  query,
  where,
  getDocs,
  collection,
} from "firebase/firestore";
import { db } from "../config/firebaseConfig";
import NavBar from "../components/NavBar";
import styles from "../styling/profilePage.module.css";

const ProfileLoadingSkeleton = () => {
  return (
    <>
      <NavBar />
      <div className={styles["loading-skeleton"]}>
        <div className={styles["skeleton-header"]}>
          <div className={styles["skeleton-header-content"]}>
            <div className={`${styles["skeleton-field"]} ${styles["title"]}`} />
            <div
              className={`${styles["skeleton-field"]} ${styles["description"]}`}
            />
          </div>
          <div className={styles["skeleton-actions"]}>
            <div className={styles["skeleton-button"]} />
            <div className={styles["skeleton-button"]} />
          </div>
        </div>

        <div className={styles["skeleton-section"]}>
          <div className={`${styles["skeleton-field"]} ${styles["medium"]}`} />
          <div className={`${styles["skeleton-field"]} ${styles["short"]}`} />
          <div className={`${styles["skeleton-field"]} ${styles["long"]}`} />
        </div>

        <div className={styles["skeleton-section"]}>
          <div className={`${styles["skeleton-field"]} ${styles["medium"]}`} />
          <div className={`${styles["skeleton-field"]} ${styles["long"]}`} />
          <div className={`${styles["skeleton-field"]} ${styles["short"]}`} />
        </div>

        <div className={styles["skeleton-section"]}>
          <div className={`${styles["skeleton-field"]} ${styles["medium"]}`} />
          <div className={`${styles["skeleton-field"]} ${styles["short"]}`} />
          <div className={`${styles["skeleton-field"]} ${styles["long"]}`} />
        </div>
      </div>
    </>
  );
};

function ProfilePage() {
  const navigate = useNavigate();
  const auth = getAuth();
  const user = auth.currentUser;

  const [formData, setFormData] = useState({
    name: "",
    industry: "",
    email: "",
    description: "",
    searchable: true,
    linkedin: "",
    externalLinks: [],
    company: "",
  });
  const [resumeDetails, setResumeDetails] = useState({
    full_name: "",
    email: "",
    phone: "",
    education: [{ degree: "", major: "", school: "", graduation_year: "" }],
    work_experience: [{ title: "", company: "", years: "" }],
    skills: [],
    clubs: [],
    certifications: [],
    languages: [],
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showCopyFeedback, setShowCopyFeedback] = useState(false);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    setUserId(user.uid);

    const fetchProfileData = async () => {
      try {
        const q = query(
          collection(db, "adonis_users"),
          where("formData.email", "==", user.email)
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          setFormData({
            ...docData.formData,
            externalLinks: docData.formData.externalLinks || [],
            company: docData.formData.company || "",
          });
          setResumeDetails(
            docData.resumeDetails || {
              full_name: "",
              email: "",
              phone: "",
              education: [
                { degree: "", major: "", school: "", graduation_year: "" },
              ],
              work_experience: [{ title: "", company: "", years: "" }],
              skills: [],
              clubs: [],
              certifications: [],
              languages: [],
            }
          );
        } else {
          setError("Profile data not found.");
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        setError("Failed to load profile data.");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [user, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleResumeChange = (e, section, index) => {
    const { name, value } = e.target;
    const updatedResumeDetails = { ...resumeDetails };

    if (Array.isArray(updatedResumeDetails[section])) {
      updatedResumeDetails[section][index] = {
        ...updatedResumeDetails[section][index],
        [name]: value,
      };
    } else {
      updatedResumeDetails[section] = value;
    }

    setResumeDetails(updatedResumeDetails);
  };

  const addResumeSection = (section) => {
    const updatedResumeDetails = { ...resumeDetails };

    switch (section) {
      case "education":
        updatedResumeDetails.education.push({
          degree: "",
          major: "",
          school: "",
          graduation_year: "",
        });
        break;
      case "work_experience":
        updatedResumeDetails.work_experience.push({
          title: "",
          company: "",
          years: "",
        });
        break;
      case "skills":
        updatedResumeDetails.skills.push("");
        break;
      case "clubs":
        updatedResumeDetails.clubs.push("");
        break;
      case "certifications":
        updatedResumeDetails.certifications.push("");
        break;
      case "languages":
        updatedResumeDetails.languages.push("");
        break;
      default:
        break;
    }

    setResumeDetails(updatedResumeDetails);
  };

  const removeResumeSection = (section, index) => {
    const updatedResumeDetails = { ...resumeDetails };

    if (Array.isArray(updatedResumeDetails[section])) {
      updatedResumeDetails[section].splice(index, 1);
      setResumeDetails(updatedResumeDetails);
    }
  };

  const handleExternalLinkChange = (index, field, value) => {
    const updatedLinks = [...formData.externalLinks];
    updatedLinks[index] = {
      ...updatedLinks[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      externalLinks: updatedLinks,
    });
  };

  const addExternalLink = () => {
    setFormData({
      ...formData,
      externalLinks: [...formData.externalLinks, { title: "", url: "" }],
    });
  };

  const removeExternalLink = (index) => {
    const updatedLinks = formData.externalLinks.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      externalLinks: updatedLinks,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    const auth = getAuth();
    const user = auth.currentUser;
    const userEmail = user ? user.email : null;
    const uid = user ? user.uid : null;

    if (!userEmail || !uid) {
      setError("User is not authenticated. Please log in.");
      setLoading(false);
      return;
    }

    try {
      // Construct searchable text based on profile information
      const profileInfo = `
        Name: ${formData.name}
        Email: ${formData.email}
        Description: ${formData.description || ""}
        Phone: ${resumeDetails.phone}
        Education: ${resumeDetails.education
          .map(
            (edu) =>
              `${edu.degree} in ${edu.major} at ${edu.school} (${edu.graduation_year})`
          )
          .join(", ")}
        Work Experience: ${resumeDetails.work_experience
          .map((work) => `${work.title} at ${work.company} (${work.years})`)
          .join(", ")}
        Skills: ${resumeDetails.skills.join(", ")}
        Clubs: ${resumeDetails.clubs.join(", ")}
        Certifications: ${resumeDetails.certifications.join(", ")}
        Languages: ${resumeDetails.languages.join(", ")}
      `;

      // Update Firestore with new profile data
      const q = query(
        collection(db, "adonis_users"),
        where("formData.email", "==", user.email)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;

        await updateDoc(docRef, {
          formData: formData,
          resumeDetails: resumeDetails,
        });

        setSuccessMessage("Profile updated successfully!");
        setIsEditing(false);
      } else {
        throw new Error("User document not found");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleShare = async () => {
    const profileUrl = `${window.location.origin}/profile/${userId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Check out my profile",
          text: "View my professional profile",
          url: profileUrl,
        });
      } catch (err) {
        // Fallback to copy if share is cancelled or fails
        copyToClipboard(profileUrl);
      }
    } else {
      // Fallback for browsers that don't support share API
      copyToClipboard(profileUrl);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setShowCopyFeedback(true);
    setTimeout(() => setShowCopyFeedback(false), 2000);
  };

  const updateSearchPreference = async (newSearchable) => {
    try {
      setFormData((prev) => ({
        ...prev,
        searchable: newSearchable,
      }));

      const q = query(
        collection(db, "adonis_users"),
        where("formData.email", "==", user.email)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, {
          "formData.searchable": newSearchable,
        });
        setSuccessMessage("Search preference updated successfully!");
      }
    } catch (error) {
      // Revert local state if update fails
      setFormData((prev) => ({
        ...prev,
        searchable: !newSearchable,
      }));
      console.error("Error updating search preference:", error);
      setError("Failed to update search preference. Please try again.");
    }
  };

  const renderEditableList = (section, title) => (
    <div className={styles["edit-section"]}>
      <h3>{title}</h3>
      <div className={styles["editable-list"]}>
        {resumeDetails[section].map((item, index) => (
          <div key={index} className={styles["editable-item"]}>
            {typeof item === "object" ? (
              <div className={styles["editable-fields"]}>
                {Object.keys(item).map((key) => (
                  <div key={key} className={styles["editable-field"]}>
                    <label>
                      {key
                        .replace(/_/g, " ")
                        .replace(/\b\w/g, (l) => l.toUpperCase())}
                    </label>
                    <input
                      type="text"
                      name={key}
                      placeholder={`Enter ${key.replace(/_/g, " ")}`}
                      value={item[key]}
                      onChange={(e) => handleResumeChange(e, section, index)}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => removeResumeSection(section, index)}
                  className={styles["remove-button"]}
                  aria-label="Remove item"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <div className={styles["editable-simple-field"]}>
                <input
                  type="text"
                  value={item}
                  placeholder={`Add ${title.slice(0, -1)}`}
                  onChange={(e) => {
                    const updatedList = [...resumeDetails[section]];
                    updatedList[index] = e.target.value;
                    setResumeDetails({
                      ...resumeDetails,
                      [section]: updatedList,
                    });
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeResumeSection(section, index)}
                  className={styles["remove-button"]}
                  aria-label="Remove item"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => addResumeSection(section)}
        className={styles["add-button"]}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add {title.slice(0, -1)}
      </button>
    </div>
  );

  const renderSkills = () => (
    <div className={styles["edit-section"]}>
      <h3>Skills</h3>
      <div className={styles["editable-list"]}>
        {resumeDetails.skills.map((skill, index) => (
          <div key={index} className={styles["editable-simple-field"]}>
            <input
              type="text"
              value={skill}
              placeholder="Add a skill"
              onChange={(e) => {
                const updatedSkills = [...resumeDetails.skills];
                updatedSkills[index] = e.target.value;
                setResumeDetails({
                  ...resumeDetails,
                  skills: updatedSkills,
                });
              }}
            />
            <button
              type="button"
              onClick={() => {
                const updatedSkills = resumeDetails.skills.filter(
                  (_, i) => i !== index
                );
                setResumeDetails({
                  ...resumeDetails,
                  skills: updatedSkills,
                });
              }}
              className={styles["remove-button"]}
              aria-label="Remove skill"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => {
          setResumeDetails({
            ...resumeDetails,
            skills: [...resumeDetails.skills, ""],
          });
        }}
        className={styles["add-button"]}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add Skill
      </button>
    </div>
  );

  const renderExperience = () => (
    <div className={styles["edit-section"]}>
      {resumeDetails.work_experience.map((exp, index) => (
        <div key={index} className={styles["experience-item"]}>
          <div className={styles["experience-header"]}>
            <div>
              <h4 className={styles["experience-title"]}>{exp.title}</h4>
              <div className={styles["experience-company"]}>{exp.company}</div>
            </div>
            <div className={styles["experience-date"]}>{exp.years}</div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderProfileDescription = () => (
    <div className={styles["edit-field"]}>
      <label htmlFor="description">
        Profile Description
        <span className={styles["character-count"]}>
          {formData.description?.length || 0}/250 characters
        </span>
      </label>
      <textarea
        id="description"
        name="description"
        value={formData.description || ""}
        onChange={(e) => {
          if (e.target.value.length <= 250) {
            handleChange(e);
          }
        }}
        placeholder="Write a brief introduction about yourself..."
        maxLength={250}
      />
    </div>
  );

  const ProfilePreference = ({ searchable, onToggle }) => (
    <div className={styles["profile-preference"]}>
      <div className={styles["preference-header"]}>
        <div className={styles["preference-info"]}>
          <h3>Profile Visibility</h3>
          <p className={styles["preference-description"]}>
            {searchable
              ? "Your profile is visible in search results"
              : "Your profile is hidden from search results"}
          </p>
        </div>
        <button
          className={`${styles["toggle-button"]} ${
            searchable ? styles["toggle-active"] : ""
          }`}
          onClick={() => onToggle(!searchable)}
        >
          {searchable ? "Visible" : "Hidden"}
        </button>
      </div>
    </div>
  );

  const ShareProfileButton = ({ userId }) => (
    <button
      onClick={handleShare}
      className={styles["share-button"]}
      aria-label="Share profile"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className={styles["share-icon"]}
      >
        <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
      {showCopyFeedback ? "Copied!" : "Share Profile"}
    </button>
  );

  const renderExternalLinks = () => (
    <div className={styles["edit-section"]}>
      <h3>External Links</h3>
      {formData.externalLinks.map((link, index) => (
        <div key={index} className={styles["editable-link"]}>
          <input
            type="text"
            placeholder="Link Title (e.g., Portfolio, GitHub)"
            value={link.title}
            onChange={(e) =>
              handleExternalLinkChange(index, "title", e.target.value)
            }
          />
          <input
            type="url"
            placeholder="https://..."
            value={link.url}
            onChange={(e) =>
              handleExternalLinkChange(index, "url", e.target.value)
            }
            pattern="https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)"
            title="Please enter a valid URL starting with http:// or https://"
          />
          <button
            type="button"
            onClick={() => removeExternalLink(index)}
            className={styles["remove-button"]}
            aria-label="Remove link"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addExternalLink}
        className={styles["add-button"]}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M12 5v14M5 12h14" />
        </svg>
        Add External Link
      </button>
    </div>
  );

  if (loading) {
    return <ProfileLoadingSkeleton />;
  }

  return (
    <>
      <NavBar />
      <div className={styles["profile-container"]}>
        <div className={styles["profile-header"]}>
          <div className={styles["profile-header-content"]}>
            <h1>{formData.name}</h1>
            <p className={styles["profile-description"]}>
              {formData.description || "No description provided"}
            </p>
            {userId && (
              <div className={styles["profile-link-container"]}>
                <ShareProfileButton userId={userId} />
              </div>
            )}
          </div>
          <div className={styles["profile-actions"]}>
            <button
              className={styles["profile-action-btn"]}
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </button>
            <button
              className={styles["profile-action-btn"]}
              onClick={handleSignOut}
            >
              Sign Out
            </button>
          </div>
        </div>

        {error && <div className={styles["error"]}>{error}</div>}
        {successMessage && (
          <div className={styles["success"]}>{successMessage}</div>
        )}

        {isEditing ? (
          <form onSubmit={handleSubmit} className={styles["edit-form"]}>
            <div className={styles["edit-section"]}>
              <h3>Account Details</h3>
              <div className={styles["edit-field"]}>
                <label htmlFor="name">Full Name</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className={styles["edit-field"]}>
                <label htmlFor="industry">Industry</label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select your industry*</option>
                  <option value="Business & Finance">Business & Finance</option>
                  <option value="Engineering & Manufacturing">
                    Engineering & Manufacturing
                  </option>
                  <option value="Technology & Software">
                    Technology & Software
                  </option>
                  <option value="Healthcare & Science">
                    Healthcare & Science
                  </option>
                  <option value="Marketing & Media">Marketing & Media</option>
                  <option value="Education & Research">
                    Education & Research
                  </option>
                  <option value="Government & Law">Government & Law</option>
                  <option value="Retail & Hospitality">
                    Retail & Hospitality
                  </option>
                  <option value="Real Estate & Construction">
                    Real Estate & Construction
                  </option>
                  <option value="Energy & Environment">
                    Energy & Environment
                  </option>
                  <option value="Arts & Entertainment">
                    Arts & Entertainment
                  </option>
                  <option value="Sports & Wellness">Sports & Wellness</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className={styles["edit-field"]}>
                <label htmlFor="company">Company</label>
                <input
                  id="company"
                  type="text"
                  name="company"
                  value={formData.company || ""}
                  onChange={handleChange}
                  placeholder="Your company or organization"
                />
              </div>
              {renderProfileDescription()}
              <div className={styles["edit-field"]}>
                <label htmlFor="linkedin">LinkedIn Profile</label>
                <input
                  id="linkedin"
                  type="url"
                  name="linkedin"
                  value={formData.linkedin || ""}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/username"
                  pattern="https?:\/\/(www\.)?linkedin\.com\/in\/.*"
                  title="Please enter a valid LinkedIn profile URL"
                />
              </div>
            </div>

            <div className={styles["edit-section"]}>
              <h2>Resume Details</h2>
              <div className={styles["edit-field"]}>
                <label htmlFor="full_name">Full Name</label>
                <input
                  id="full_name"
                  type="text"
                  name="full_name"
                  value={resumeDetails.full_name}
                  onChange={(e) => handleResumeChange(e, "full_name")}
                />
              </div>
              <div className={styles["edit-field"]}>
                <label htmlFor="phone">Phone Number</label>
                <input
                  id="phone"
                  type="text"
                  name="phone"
                  value={resumeDetails.phone}
                  onChange={(e) => handleResumeChange(e, "phone")}
                />
              </div>
              {renderEditableList("education", "Education")}
              {renderEditableList("work_experience", "Work Experiences")}
              {renderSkills()}
              {renderExperience()}
              {renderEditableList("clubs", "Clubs")}
              {renderEditableList("certifications", "Certification & Awards")}
              {renderEditableList("languages", "Languages")}
            </div>

            {renderExternalLinks()}

            <div className={styles["edit-actions"]}>
              <button
                type="submit"
                className={styles["save-btn"]}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                className={styles["cancel-btn"]}
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className={styles["profile-section"]}>
              <h2>Account Details</h2>
              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>Name</div>
                <div className={styles["profile-field-value"]}>
                  {formData.name}
                </div>
              </div>
              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>Email</div>
                <div className={styles["profile-field-value"]}>
                  {formData.email}
                </div>
              </div>
              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>Industry</div>
                <div className={styles["profile-field-value"]}>
                  {formData.industry}
                </div>
              </div>
              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>Company</div>
                <div className={styles["profile-field-value"]}>
                  {formData.company || "Not provided"}
                </div>
              </div>
              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>LinkedIn</div>
                <div className={styles["profile-field-value"]}>
                  {formData.linkedin ? (
                    <a
                      href={formData.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles["linkedin-link"]}
                    >
                      View Profile
                    </a>
                  ) : (
                    "Not provided"
                  )}
                </div>
              </div>
            </div>

            <div className={styles["profile-section"]}>
              <h2>Resume Details</h2>
              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>Contact</div>
                <div className={styles["profile-field-value"]}>
                  <div>{resumeDetails.full_name}</div>
                  <div>{resumeDetails.email}</div>
                  <div>{resumeDetails.phone}</div>
                </div>
              </div>

              <div className={styles["profile-field"]}>
                <div className={styles["profile-field-label"]}>Education</div>
                <div className={styles["profile-field-value"]}>
                  {resumeDetails.education.map((edu, index) => (
                    <div key={index} className={styles["profile-list-item"]}>
                      {edu.degree} in {edu.major} at {edu.school} (
                      {edu.graduation_year})
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles["profile-section"]}>
              <ProfilePreference
                searchable={formData.searchable}
                onToggle={updateSearchPreference}
              />
            </div>

            <div className={styles["profile-section"]}>
              <h2>External Links</h2>
              {formData.externalLinks?.length > 0 ? (
                <div className={styles["external-links"]}>
                  {formData.externalLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles["external-link"]}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      {link.title}
                    </a>
                  ))}
                </div>
              ) : (
                <div className={styles["profile-field-value"]}>
                  No external links added
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default ProfilePage;
